import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PublishIcon from '@mui/icons-material/Publish';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import VerifiedIcon from '@mui/icons-material/Verified';
import type {
  CliBlockSubtypePromotionData,
  CliBlockSubtypePromotionRollbackData,
  CliBlockSubtypeRecord,
} from '@modules/shared-types';
import { StyledTextField } from '@modules/ui-components';
import {
  SDataTable,
  type SDataTableColumnDef,
} from '@modules/ui-components/dialogs/SDataTable';
import { PROMPT_SECTIONS_ADMIN } from '@test-kit/selectors/prompt-sections-admin';
import type { PromptSectionsAdminApiService } from '../services/PromptSectionsAdminApiService';

const DEFAULT_PROJECT_ID = 'FINo7LxKWBPXHochQK0n';
const TARGET_TYPE_PATTERN = /^[a-z][a-z0-9_]{1,79}$/;

type PromotionStage =
  | 'target'
  | 'dry_run'
  | 'pending_schema'
  | 'completed'
  | 'rollback'
  | 'rolled_back';

export interface BlockSubtypePromotionPanelProps {
  api: PromptSectionsAdminApiService;
}

/** B6-I2 strict-admin UI over the checksum-bound subtype promotion state machine. */
export function BlockSubtypePromotionPanel({
  api,
}: BlockSubtypePromotionPanelProps): React.ReactElement {
  const [projectId, setProjectId] = useState(DEFAULT_PROJECT_ID);
  const [subtypes, setSubtypes] = useState<CliBlockSubtypeRecord[]>([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [selectedSubtype, setSelectedSubtype] = useState<CliBlockSubtypeRecord | null>(null);
  const [targetType, setTargetType] = useState('');
  const [stage, setStage] = useState<PromotionStage>('target');
  const [plan, setPlan] = useState<CliBlockSubtypePromotionData | null>(null);
  const [promotionResult, setPromotionResult] = useState<CliBlockSubtypePromotionData | null>(null);
  const [rollbackPlan, setRollbackPlan] = useState<CliBlockSubtypePromotionRollbackData | null>(null);
  const [rollbackResult, setRollbackResult] = useState<CliBlockSubtypePromotionRollbackData | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const rankedSubtypes = useMemo(
    () => [...subtypes].sort((left, right) => (
      (right.usageCount ?? 0) - (left.usageCount ?? 0)
      || left.name.localeCompare(right.name)
      || left.id.localeCompare(right.id)
    )),
    [subtypes],
  );

  const columns = useMemo<SDataTableColumnDef<CliBlockSubtypeRecord>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Subtype',
      size: 420,
      Cell: ({ row }) => (
        <Box>
          <Typography fontWeight={650}>{row.original.name}</Typography>
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
            {row.original.slug}
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
            <Chip size="small" label={`${row.original.defaultInstructions.length} instructions`} />
            <Chip size="small" label={`playbook ${row.original.playbook.length} chars`} />
          </Stack>
        </Box>
      ),
    },
    {
      accessorKey: 'usageCount',
      header: 'Usage',
      size: 120,
      Cell: ({ row }) => (
        <Typography fontWeight={650}>{row.original.usageCount ?? 0} blocks</Typography>
      ),
    },
    {
      id: 'readiness',
      header: 'Readiness',
      size: 180,
      enableSorting: false,
      Cell: ({ row }) => {
        const readiness = candidateReadiness(row.original);
        return (
          <Chip
            size="small"
            color={readiness.ready ? 'success' : 'default'}
            variant={readiness.ready ? 'outlined' : 'filled'}
            label={readiness.label}
          />
        );
      },
    },
    {
      id: 'actions',
      header: 'Action',
      size: 120,
      enableSorting: false,
      Cell: ({ row }) => (
        <Button
          data-testid={PROMPT_SECTIONS_ADMIN.promotionOpen(row.original.id)}
          size="small"
          variant="outlined"
          disabled={busy || !candidateReadiness(row.original).ready}
          onClick={(event) => {
            event.stopPropagation();
            openCandidate(row.original);
          }}
        >
          Review
        </Button>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [busy]);

  async function loadCandidates(): Promise<void> {
    const normalizedProjectId = projectId.trim();
    if (!normalizedProjectId) {
      setError('Select a project before loading promotion candidates.');
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const result = await api.listProjectSubtypes(normalizedProjectId);
      setSubtypes(result.subtypes);
      setCatalogLoaded(true);
      setStatus(`Loaded ${result.subtypes.length} subtype candidate(s).`);
    } catch (loadError) {
      setCatalogLoaded(false);
      setError(errorMessage(loadError));
    } finally {
      setBusy(false);
    }
  }

  function openCandidate(subtype: CliBlockSubtypeRecord): void {
    setSelectedSubtype(subtype);
    setTargetType(subtype.slug);
    setStage('target');
    setPlan(null);
    setPromotionResult(null);
    setRollbackPlan(null);
    setRollbackResult(null);
    setConfirmation('');
    setError(null);
    setStatus(null);
    setDrawerOpen(true);
  }

  async function runPromotionDryRun(): Promise<void> {
    if (!selectedSubtype || !TARGET_TYPE_PATTERN.test(targetType)) {
      setError('Target type must be a lowercase snake_case BlockType value.');
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const result = await api.promoteSubtype({
        projectId: projectId.trim(),
        subtypeId: selectedSubtype.id,
        targetType,
        dryRun: true,
      });
      setPlan(result);
      setConfirmation('');
      setRollbackPlan(null);
      setRollbackResult(null);
      if (result.status === 'already_promoted') {
        setPromotionResult(result);
        setStage('completed');
        setStatus('Read-only verification confirmed that this target is already promoted.');
      } else {
        setPromotionResult(null);
        setStage('dry_run');
        setStatus(`Zero-write dry-run ready · ${result.dryRunChecksum}`);
      }
    } catch (dryRunError) {
      setError(errorMessage(dryRunError));
    } finally {
      setBusy(false);
    }
  }

  async function applyPromotion(): Promise<void> {
    if (!selectedSubtype || !plan) return;
    if (confirmation !== plan.confirmation) {
      setError('Confirmation must exactly match the current dry-run receipt.');
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const result = await api.promoteSubtype({
        projectId: projectId.trim(),
        subtypeId: selectedSubtype.id,
        targetType: plan.targetType,
        dryRun: false,
        apply: true,
        confirmation: plan.confirmation,
        dryRunChecksum: plan.dryRunChecksum,
      });
      setPromotionResult(result);
      setConfirmation('');
      if (result.status === 'pending_schema') {
        setStage('pending_schema');
        setStatus('Promotion parked safely in pending_schema. No prompts or blocks were changed.');
      } else {
        setStage('completed');
        setStatus(result.status === 'already_promoted'
          ? 'Promotion was already complete.'
          : 'Named block type promotion completed.');
      }
    } catch (applyError) {
      setError(errorMessage(applyError));
    } finally {
      setBusy(false);
    }
  }

  async function prepareRollback(): Promise<void> {
    if (!selectedSubtype || !plan) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const result = await api.rollbackSubtype({
        projectId: projectId.trim(),
        subtypeId: selectedSubtype.id,
        targetType: plan.targetType,
        dryRun: true,
      });
      setRollbackPlan(result);
      setConfirmation('');
      if (result.status === 'already_rolled_back') {
        setRollbackResult(result);
        setStage('rolled_back');
        setStatus('Read-only verification confirmed that this promotion was already rolled back.');
      } else {
        setStage('rollback');
        setStatus(`Zero-write rollback plan ready · ${result.dryRunChecksum}`);
      }
    } catch (rollbackError) {
      setError(errorMessage(rollbackError));
    } finally {
      setBusy(false);
    }
  }

  async function applyRollback(): Promise<void> {
    if (!selectedSubtype || !rollbackPlan) return;
    if (confirmation !== rollbackPlan.confirmation) {
      setError('Confirmation must exactly match the current rollback receipt.');
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const result = await api.rollbackSubtype({
        projectId: projectId.trim(),
        subtypeId: selectedSubtype.id,
        targetType: rollbackPlan.targetType,
        dryRun: false,
        apply: true,
        confirmation: rollbackPlan.confirmation,
        dryRunChecksum: rollbackPlan.dryRunChecksum,
      });
      setRollbackResult(result);
      setConfirmation('');
      setStage('rolled_back');
      setStatus(result.status === 'already_rolled_back'
        ? 'Promotion was already rolled back.'
        : 'Promotion rollback completed. The schema enum remains inert.');
    } catch (rollbackError) {
      setError(errorMessage(rollbackError));
    } finally {
      setBusy(false);
    }
  }

  const blockingCollisions = plan?.collisions.filter((collision) => collision.blocking) ?? [];
  const activeStep = stage === 'target'
    ? 0
    : stage === 'dry_run'
      ? 1
      : stage === 'pending_schema'
        ? 2
        : 3;

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {status && <Alert severity="success">{status}</Alert>}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'flex-end' }}
      >
        <Box sx={{ flex: 1, maxWidth: 620 }}>
          <StyledTextField
            data-testid={PROMPT_SECTIONS_ADMIN.promotionProjectId}
            value={projectId}
            onChange={(value) => {
              setProjectId(value);
              setCatalogLoaded(false);
              setSubtypes([]);
            }}
            label="Source project id"
            helperText="Candidates and block migration remain scoped to this project."
            fullWidth
          />
        </Box>
        <Button
          data-testid={PROMPT_SECTIONS_ADMIN.promotionLoad}
          variant="outlined"
          startIcon={busy ? <CircularProgress size={16} /> : <RefreshIcon />}
          disabled={busy || !projectId.trim()}
          onClick={() => void loadCandidates()}
        >
          Load candidates
        </Button>
      </Stack>

      <Box>
        <Typography variant="h6">Promotion candidates</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Ranked by observed usage. Disabled, unused, or playbook-less subtypes cannot enter the wizard.
        </Typography>
        <SDataTable<CliBlockSubtypeRecord>
          data={rankedSubtypes}
          columns={columns}
          getRowId={(row) => row.id}
          enablePagination={false}
          enableTopToolbar={false}
          state={{ isLoading: busy && !catalogLoaded }}
          muiTablePaperProps={{
            variant: 'outlined',
            elevation: 0,
            'data-testid': PROMPT_SECTIONS_ADMIN.promotionList,
          } as any}
          muiTableBodyRowProps={({ row }) => ({
            'data-testid': PROMPT_SECTIONS_ADMIN.promotionCandidate(row.original.id),
          } as any)}
          renderEmptyRowsFallback={() => (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              {catalogLoaded ? 'No project subtypes.' : 'Load a project to rank candidates.'}
            </Typography>
          )}
        />
      </Box>

      <Alert severity="info">
        Promotion is strict-admin only. Other projects with the same subtype slug are reported read-only and are never migrated by this flow.
      </Alert>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => !busy && setDrawerOpen(false)}
        PaperProps={{
          'data-testid': PROMPT_SECTIONS_ADMIN.promotionDrawer,
          sx: { width: { xs: '100%', md: 760 }, maxWidth: '100vw' },
        }}
      >
        <Stack sx={{ minHeight: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 68, px: 2 }}>
            <IconButton
              data-testid={PROMPT_SECTIONS_ADMIN.promotionClose}
              aria-label="Close subtype promotion"
              onClick={() => setDrawerOpen(false)}
              disabled={busy}
            >
              <CloseIcon />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6">Promote subtype</Typography>
              <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                {selectedSubtype?.slug ?? 'No candidate selected'}
              </Typography>
            </Box>
            <Chip size="small" label={stageLabel(stage)} variant="outlined" />
          </Stack>
          <Divider />

          <Box sx={{ px: 2.5, pt: 2.5 }}>
            <Stepper data-testid={PROMPT_SECTIONS_ADMIN.promotionStepper} activeStep={activeStep} alternativeLabel>
              {['Target', 'Dry-run', 'Schema', 'Complete'].map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>
          </Box>

          <Stack spacing={2} sx={{ p: 2.5, flex: 1, overflowY: 'auto' }}>
            {stage === 'target' && selectedSubtype && (
              <>
                <Box>
                  <Typography variant="h6">Choose the named type</Typography>
                  <Typography variant="body2" color="text.secondary">
                    The project subtype stays unchanged until the checksum-bound lifecycle completes.
                  </Typography>
                </Box>
                <SummaryGrid items={[
                  ['Observed use', `${selectedSubtype.usageCount ?? 0} blocks`],
                  ['Playbook', `${selectedSubtype.playbook.length} characters`],
                  ['Instructions', String(selectedSubtype.defaultInstructions.length)],
                  ['Subtype state', selectedSubtype.enabled ? 'Enabled' : 'Disabled'],
                ]} />
                <StyledTextField
                  data-testid={PROMPT_SECTIONS_ADMIN.promotionTargetType}
                  value={targetType}
                  onChange={(value) => {
                    setTargetType(value);
                    setError(null);
                  }}
                  label="New BlockType value"
                  helperText="Lowercase snake_case. The first action is guaranteed zero-write."
                  error={Boolean(targetType && !TARGET_TYPE_PATTERN.test(targetType))}
                  errorMessage={targetType && !TARGET_TYPE_PATTERN.test(targetType)
                    ? 'Use lowercase letters, numbers, and underscores.'
                    : undefined}
                  fullWidth
                />
                <Alert severity="info">
                  Dry-run computes collisions, prompt drafts, affected paths, schema checklist, exact confirmation text, and SHA-256 receipt without writes.
                </Alert>
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    data-testid={PROMPT_SECTIONS_ADMIN.promotionDryRun}
                    variant="contained"
                    startIcon={<VerifiedIcon />}
                    disabled={busy || !TARGET_TYPE_PATTERN.test(targetType)}
                    onClick={() => void runPromotionDryRun()}
                  >
                    Run zero-write dry-run
                  </Button>
                </Stack>
              </>
            )}

            {stage === 'dry_run' && plan && (
              <>
                <Box>
                  <Typography variant="h6">Review zero-write plan</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Approval is bound to the current schema, prompt pointers, subtype content, and migration journal.
                  </Typography>
                </Box>
                {blockingCollisions.length === 0 ? (
                  <Alert severity="success">No blocking collisions. The dry-run performed zero writes.</Alert>
                ) : blockingCollisions.map((collision) => (
                  <Alert key={`${collision.surface}:${collision.code}`} severity="error">
                    {collision.surface} · {collision.detail}
                  </Alert>
                ))}
                <SummaryGrid items={[
                  ['Source project blocks', String(plan.affected.sourceProjectBlocks)],
                  ['Other projects', String(plan.affected.otherProjects.length)],
                  ['Schema enum', plan.schemaPresent ? 'Detected' : 'Not present'],
                  ['Writes performed', String(sumPromotionWrites(plan))],
                ]} />
                <Receipt data={plan} />
                <Box component="details" sx={{ '& summary': { color: 'primary.main', cursor: 'pointer' } }}>
                  <Typography component="summary" fontWeight={650}>Prompt drafts and affected paths</Typography>
                  <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    <DraftPreview label={plan.promptDraft.path} content={plan.promptDraft.content} />
                    <DraftPreview label={plan.strategistTeachingDraft.path} content={plan.strategistTeachingDraft.content} />
                    <DraftPreview label="Source-project block paths" content={plan.affected.sourceProjectPaths.join('\n') || 'No affected blocks'} />
                  </Stack>
                </Box>
                {!plan.schemaPresent && (
                  <Alert severity="warning">
                    The next action creates only the durable `pending_schema` claim and audit receipt. It does not publish prompts or migrate blocks.
                  </Alert>
                )}
                {plan.schemaPresent && (
                  <Alert severity="success">
                    Runtime detects the approved enum. This fresh receipt may complete prompt publication and source-project migration.
                  </Alert>
                )}
                <StyledTextField
                  data-testid={PROMPT_SECTIONS_ADMIN.promotionConfirmation}
                  value={confirmation}
                  onChange={(value) => {
                    setConfirmation(value);
                    setError(null);
                  }}
                  label="Exact confirmation"
                  helperText={plan.confirmation}
                  fullWidth
                />
                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Button
                    disabled={busy}
                    onClick={() => {
                      setStage('target');
                      setPlan(null);
                      setConfirmation('');
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    data-testid={plan.schemaPresent
                      ? PROMPT_SECTIONS_ADMIN.promotionCompleteApply
                      : PROMPT_SECTIONS_ADMIN.promotionPendingApply}
                    variant="contained"
                    startIcon={<PublishIcon />}
                    disabled={busy || blockingCollisions.length > 0 || confirmation !== plan.confirmation}
                    onClick={() => void applyPromotion()}
                  >
                    {plan.schemaPresent ? 'Complete promotion' : 'Create pending schema record'}
                  </Button>
                </Stack>
              </>
            )}

            {stage === 'pending_schema' && plan && promotionResult && (
              <>
                <Box>
                  <Typography variant="h6">Waiting for schema-models</Typography>
                  <Typography variant="body2" color="text.secondary">
                    The target claim is durable. Studio cannot edit, build, or install the external schema package.
                  </Typography>
                </Box>
                <Alert severity="warning">
                  pending_schema · {promotionResult.writes.auditDocuments} audit documents · {promotionResult.writes.targetClaimWrites} target claim write
                </Alert>
                <Stack
                  data-testid={PROMPT_SECTIONS_ADMIN.promotionSchemaChecklist}
                  divider={<Divider flexItem />}
                >
                  {plan.schemaChecklist.map((item, index) => (
                    <Stack key={item} direction="row" spacing={1.25} sx={{ py: 1.25 }}>
                      <Chip size="small" label={index + 1} />
                      <Typography>{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
                <Alert severity="info">
                  After deploying the schema-bearing runtime, refresh this gate. A fresh dry-run is mandatory before completion.
                </Alert>
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    data-testid={PROMPT_SECTIONS_ADMIN.promotionSchemaRefresh}
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    disabled={busy}
                    onClick={() => void runPromotionDryRun()}
                  >
                    Refresh runtime schema
                  </Button>
                </Stack>
              </>
            )}

            {stage === 'completed' && plan && promotionResult && (
              <>
                <Box>
                  <Typography variant="h6">Promotion completed</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Named type, playbook, strategist teaching, block journal, and audit identity agree.
                  </Typography>
                </Box>
                <Alert severity="success">
                  {plan.targetType} is active. The source subtype remains available to other projects as a project-owned subtype.
                </Alert>
                <Box data-testid={PROMPT_SECTIONS_ADMIN.promotionCompletedReceipt}>
                  <SummaryGrid items={[
                    ['Playbook version', versionLabel(promotionResult.promptRegistryVersion)],
                    ['Teaching version', versionLabel(promotionResult.strategistTeachingRegistryVersion)],
                    ['Migrated blocks', String(promotionResult.writes.migratedBlocks)],
                    ['Audit documents', String(promotionResult.writes.auditDocuments)],
                  ]} />
                  <Receipt data={plan} />
                </Box>
                <Alert severity="info">
                  Rollback has its own zero-write receipt. It restores prompt pointers and project blocks while leaving the enum value inert.
                </Alert>
                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Button
                    data-testid={PROMPT_SECTIONS_ADMIN.promotionRollbackDryRun}
                    color="warning"
                    variant="outlined"
                    startIcon={<RestoreIcon />}
                    disabled={busy}
                    onClick={() => void prepareRollback()}
                  >
                    Prepare rollback dry-run
                  </Button>
                  <Button variant="contained" onClick={() => setDrawerOpen(false)}>Done</Button>
                </Stack>
              </>
            )}

            {stage === 'rollback' && rollbackPlan && (
              <>
                <Box>
                  <Typography variant="h6">Review rollback plan</Typography>
                  <Typography variant="body2" color="text.secondary">
                    This receipt is bound to the current prompt pointers and migration-journal state.
                  </Typography>
                </Box>
                <Alert severity="warning">
                  Rollback restores {rollbackPlan.affected.restorableBlocks} block(s), the playbook pointer, and strategist teaching. It does not remove the schema enum.
                </Alert>
                <SummaryGrid items={[
                  ['Journal blocks', String(rollbackPlan.affected.journalBlocks)],
                  ['Restorable blocks', String(rollbackPlan.affected.restorableBlocks)],
                  ['Already restored', String(rollbackPlan.affected.alreadyRestoredBlocks)],
                  ['Writes performed', String(sumRollbackWrites(rollbackPlan))],
                ]} />
                <Box data-testid={PROMPT_SECTIONS_ADMIN.promotionReceipt}>
                  <ReceiptRow label="Restore path" value={rollbackPlan.playbookRestore.path} />
                  <ReceiptRow label="Receipt" value={rollbackPlan.dryRunChecksum} />
                </Box>
                <StyledTextField
                  data-testid={PROMPT_SECTIONS_ADMIN.promotionConfirmation}
                  value={confirmation}
                  onChange={(value) => {
                    setConfirmation(value);
                    setError(null);
                  }}
                  label="Exact rollback confirmation"
                  helperText={rollbackPlan.confirmation}
                  fullWidth
                />
                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Button disabled={busy} onClick={() => setStage('completed')}>Back</Button>
                  <Button
                    data-testid={PROMPT_SECTIONS_ADMIN.promotionRollbackApply}
                    color="error"
                    variant="contained"
                    startIcon={<RestoreIcon />}
                    disabled={busy || confirmation !== rollbackPlan.confirmation}
                    onClick={() => void applyRollback()}
                  >
                    Apply rollback
                  </Button>
                </Stack>
              </>
            )}

            {stage === 'rolled_back' && rollbackResult && (
              <>
                <Box>
                  <Typography variant="h6">Promotion rolled back</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Project blocks and prompt pointers were restored. The named enum remains inert.
                  </Typography>
                </Box>
                <Alert severity="success">Rollback completed with an audited receipt.</Alert>
                <Box data-testid={PROMPT_SECTIONS_ADMIN.promotionRolledBackReceipt}>
                  <SummaryGrid items={[
                    ['Restored blocks', String(rollbackResult.writes.restoredBlocks)],
                    ['Prompt restores', String(rollbackResult.writes.promptPointerRestores)],
                    ['Audit documents', String(rollbackResult.writes.auditDocuments)],
                    ['Target claim writes', String(rollbackResult.writes.targetClaimWrites)],
                  ]} />
                  <ReceiptRow label="Receipt" value={rollbackResult.dryRunChecksum} />
                </Box>
                <Stack direction="row" justifyContent="flex-end">
                  <Button variant="contained" onClick={() => setDrawerOpen(false)}>Done</Button>
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </Drawer>
    </Stack>
  );
}

function candidateReadiness(subtype: CliBlockSubtypeRecord): { ready: boolean; label: string } {
  if (!subtype.enabled) return { ready: false, label: 'Disabled' };
  if (!subtype.playbook.trim()) return { ready: false, label: 'Playbook missing' };
  if ((subtype.usageCount ?? 0) < 1) return { ready: false, label: 'No observed usage' };
  return { ready: true, label: 'Ready to review' };
}

function SummaryGrid({ items }: { items: Array<[string, string]> }): React.ReactElement {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
      {items.map(([label, value]) => (
        <PaperSummary key={label} label={label} value={value} />
      ))}
    </Box>
  );
}

function PaperSummary({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography fontWeight={650}>{value}</Typography>
    </Box>
  );
}

function Receipt({ data }: { data: CliBlockSubtypePromotionData }): React.ReactElement {
  return (
    <Box
      data-testid={PROMPT_SECTIONS_ADMIN.promotionReceipt}
      sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
    >
      <ReceiptRow label="Promotion id" value={data.promotionId} />
      <ReceiptRow label="Target" value={data.targetType} />
      <ReceiptRow label="Playbook" value={data.promptDraft.path} />
      <ReceiptRow label="Teaching" value={data.strategistTeachingDraft.path} />
      <ReceiptRow label="Receipt" value={data.dryRunChecksum} />
    </Box>
  );
}

function ReceiptRow({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '130px minmax(0, 1fr)', gap: 1, py: 0.5 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="caption" fontFamily="monospace" sx={{ overflowWrap: 'anywhere' }}>{value}</Typography>
    </Box>
  );
}

function DraftPreview({ label, content }: { label: string; content: string }): React.ReactElement {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontFamily="monospace">{label}</Typography>
      <Box sx={{ p: 1.25, mt: 0.5, bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'pre-wrap', maxHeight: 180, overflow: 'auto' }}>
        <Typography component="pre" variant="caption" fontFamily="monospace" sx={{ m: 0, whiteSpace: 'pre-wrap' }}>
          {content}
        </Typography>
      </Box>
    </Box>
  );
}

function stageLabel(stage: PromotionStage): string {
  switch (stage) {
    case 'target': return 'Candidate';
    case 'dry_run': return 'Dry-run ready';
    case 'pending_schema': return 'pending_schema';
    case 'completed': return 'Completed';
    case 'rollback': return 'Rollback dry-run';
    case 'rolled_back': return 'Rolled back';
  }
}

function sumPromotionWrites(data: CliBlockSubtypePromotionData): number {
  return data.writes.auditDocuments
    + data.writes.targetClaimWrites
    + data.writes.promptRegistryVersions
    + data.writes.migratedBlocks;
}

function sumRollbackWrites(data: CliBlockSubtypePromotionRollbackData): number {
  return data.writes.auditDocuments
    + data.writes.targetClaimWrites
    + data.writes.promptPointerRestores
    + data.writes.promptRegistryVersions
    + data.writes.restoredBlocks;
}

function versionLabel(version: number | undefined): string {
  return version === undefined ? 'Verified existing' : `v${version}`;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Block subtype promotion operation failed.';
}
